// The capability vocabulary (bead hf-e3-convex-boundary-zf7.3.1).
//
// EVERY private Convex function names one of these. The boundary wrappers resolve
// identity -> Membership -> capability and hand domain code an already-authorized
// context; nothing below is ever decided in the client, in a token claim, or in route
// data (AUT-006, AUT-007, invariant 8).
//
// WHY CAPABILITIES AND NOT ROLES. The plan is explicit: "Do not use one `is_admin`
// boolean as the complete authorisation model. Define explicit capabilities even
// though the V1 UI exposes only Organiser, Member and a view-only preset" (§9.4).
// Roles are a UI vocabulary; capabilities are the enforcement vocabulary. Keeping
// them separate is what lets "view-only" be a permission preset rather than a third
// onboarding role (PER-004), and what makes a post-MVP item-limited helper possible
// without reopening the model (§6.3).
//
// THE AUTHORITY for the list is PER-008, which requires separate capabilities for:
// view plan, manage plan, view availability, manage own availability, manage people,
// record proxy action, view restricted notes, export, and close circle. Every one of
// those appears below, and `capabilities.test.ts` asserts the mapping so the
// requirement cannot quietly lose a member.
//
// NAMING: noun.verb, with an optional `.self` / `.proxy` scope suffix. The plan uses
// exactly this shape in its own examples — `plan.view` (§27) and
// `commitment.accept.self` (§36.5 function inventory).

/** Every capability in the system. Adding one here is a deliberate act. */
export const CAPABILITIES = {
  /* ---- Plan (the circle's schedule as a whole) ---------------------------------- */

  /** See the plan: events, needs and derived coverage. PER-008 "view plan". */
  PLAN_VIEW: "plan.view",
  /** Change circle-level settings — timezone, quiet hours, reminder defaults. */
  PLAN_MANAGE: "plan.manage",
  /** Create an opaque publicPlanRef share link (SHA-*). */
  PLAN_SHARE: "plan.share",
  /** Export the plan (ICS, print, portability). PER-008 "export". */
  PLAN_EXPORT: "plan.export",
  /** Close the circle. PER-008 "close circle". Deliberately its own capability. */
  PLAN_CLOSE: "plan.close",

  /* ---- Events and needs --------------------------------------------------------- */

  EVENT_CREATE: "event.create",
  EVENT_EDIT: "event.edit",
  /** Cancellation is NOT deletion (invariant 6) — hence its own capability. */
  EVENT_CANCEL: "event.cancel",

  NEED_CREATE: "need.create",
  NEED_EDIT: "need.edit",
  NEED_CANCEL: "need.cancel",

  /* ---- Availability -------------------------------------------------------------- */

  /** See who is available. PER-008 "view availability". */
  AVAILABILITY_VIEW: "availability.view",
  /** Record MY OWN availability. PER-008 "manage own availability". */
  AVAILABILITY_RECORD_SELF: "availability.record.self",
  /**
   * Record availability ON BEHALF of someone else — the WhatsApp-only member whose
   * organiser enters what they agreed (§7.2). Separate from `.self` because it is a
   * different act with different consent requirements (AVL-015, PER-008 "record
   * proxy action"), and because an organiser must never silently impersonate
   * a member (PER-012): proxy records are attributed, never anonymous.
   */
  AVAILABILITY_RECORD_PROXY: "availability.record.proxy",

  /* ---- Requests and commitments -------------------------------------------------- */

  /** Ask a specific person to take a need. Zero coverage until accepted (invariant 4). */
  REQUEST_SEND: "request.send",
  /** Accept a need for MYSELF. The plan's own example capability. */
  COMMITMENT_ACCEPT_SELF: "commitment.accept.self",
  /** Withdraw my own commitment. Never requires an excuse. */
  COMMITMENT_WITHDRAW_SELF: "commitment.withdraw.self",
  /** Record that someone else agreed, offline. Attributed (COM-011, PER-012). */
  COMMITMENT_RECORD_PROXY: "commitment.record.proxy",
  /** Mark the outcome of a commitment once its window has passed. */
  COMMITMENT_COMPLETE: "commitment.complete",

  /* ---- Practical updates --------------------------------------------------------- */

  /** Add, edit or redact MY OWN short operational update (D-081). */
  PRACTICAL_UPDATE_WRITE_SELF: "practical.write.self",
  /** Transparently record or maintain an update on somebody else's behalf (PER-012). */
  PRACTICAL_UPDATE_WRITE_PROXY: "practical.write.proxy",

  /* ---- People and membership ------------------------------------------------------ */

  PEOPLE_VIEW: "people.view",
  /** Invite, remove, convert offline people, set presets. PER-008 "manage people". */
  PEOPLE_MANAGE: "people.manage",
  /**
   * See contact destinations unmasked. Separate from PEOPLE_MANAGE because seeing a
   * phone number and administering a list are different privileges (PER-013).
   */
  PEOPLE_VIEW_CONTACT_DETAILS: "people.viewContactDetails",

  /* ---- Restricted content and audit ------------------------------------------------ */

  /** Notes an organiser marked restricted. PER-008 "view restricted notes". */
  NOTES_VIEW_RESTRICTED: "notes.viewRestricted",
  /** Read the plain-language change history (ADM-001). */
  AUDIT_VIEW: "audit.view",
} as const;

export type Capability = (typeof CAPABILITIES)[keyof typeof CAPABILITIES];

/** Every capability, for exhaustiveness checks and the doc table. */
export const ALL_CAPABILITIES: readonly Capability[] = Object.values(CAPABILITIES);

/* ================================================================= archetypes === */

/**
 * The membership archetypes the fixtures model.
 *
 * These are DEFAULT capability sets, not roles stored on a record. A Membership
 * carries an explicit capability array (§9.4); an archetype is just the preset an
 * organiser starts from. That distinction is what PER-004 requires — view-only is a
 * permission preset, not a third role — and it is why the boundary never asks "is
 * this person an organiser?", only "does this membership hold this capability?".
 */
export type MembershipArchetype = "organiser" | "member" | "viewOnly" | "proxyRecorder";

/** An ordinary Member: takes part in the rota, manages their own availability. */
const MEMBER_CAPABILITIES: readonly Capability[] = [
  CAPABILITIES.PLAN_VIEW,
  CAPABILITIES.PEOPLE_VIEW,
  CAPABILITIES.AVAILABILITY_VIEW,
  CAPABILITIES.AVAILABILITY_RECORD_SELF,
  CAPABILITIES.COMMITMENT_ACCEPT_SELF,
  CAPABILITIES.COMMITMENT_WITHDRAW_SELF,
  CAPABILITIES.COMMITMENT_COMPLETE,
  CAPABILITIES.PRACTICAL_UPDATE_WRITE_SELF,
  CAPABILITIES.PLAN_EXPORT,
];

/**
 * View-only: awareness without operational action.
 *
 * The plan lists what such a member CANNOT do (§6.4): record availability,
 * volunteer, edit data, see restricted notes, or manage people. This set is that
 * sentence, subtracted — and `capabilities.test.ts` asserts each exclusion by name,
 * because the value of a least-privilege preset is entirely in what it omits.
 */
const VIEW_ONLY_CAPABILITIES: readonly Capability[] = [
  CAPABILITIES.PLAN_VIEW,
  CAPABILITIES.PEOPLE_VIEW,
  CAPABILITIES.AVAILABILITY_VIEW,
];

/**
 * Organiser: the full V1 preset.
 *
 * Everything, including the proxy capabilities — an organiser is exactly who records
 * what the WhatsApp-only member agreed. It is a preset, not a superuser flag: the
 * boundary still checks the individual capability on every call, so an organiser who
 * loses `plan.close` loses it immediately.
 */
const ORGANISER_CAPABILITIES: readonly Capability[] = ALL_CAPABILITIES;

/**
 * Proxy recorder: a member trusted to record on others' behalf, without full
 * organiser administration.
 *
 * Not a V1 UI preset — the V1 vocabulary is Organiser and Member (PER-003) — but the
 * model must express it, because "record on behalf" and "manage people" are genuinely
 * different privileges and the plan says the authorisation model should not prevent
 * finer grants later (§6.3). It exists so that separation is real rather than
 * theoretical.
 */
const PROXY_RECORDER_CAPABILITIES: readonly Capability[] = [
  ...MEMBER_CAPABILITIES,
  CAPABILITIES.AVAILABILITY_RECORD_PROXY,
  CAPABILITIES.COMMITMENT_RECORD_PROXY,
  CAPABILITIES.PRACTICAL_UPDATE_WRITE_PROXY,
  CAPABILITIES.PEOPLE_VIEW_CONTACT_DETAILS,
];

export const ARCHETYPE_CAPABILITIES: Readonly<Record<MembershipArchetype, readonly Capability[]>> =
  {
    organiser: ORGANISER_CAPABILITIES,
    member: MEMBER_CAPABILITIES,
    viewOnly: VIEW_ONLY_CAPABILITIES,
    proxyRecorder: PROXY_RECORDER_CAPABILITIES,
  };

/**
 * Does this membership hold this capability?
 *
 * Takes the membership's OWN capability array, never an archetype: the archetype is
 * a starting preset, and the record is the authority (§9.4). A boundary that checked
 * the archetype would silently ignore a capability an organiser had removed.
 */
export function hasCapability(held: readonly Capability[], required: Capability): boolean {
  return held.includes(required);
}

/**
 * Whether one Membership may place this explicit capability set on another.
 *
 * The record, not its preset label, is authoritative (§9.4). A people manager may
 * delegate only authority they currently hold themselves; otherwise
 * `people.manage` collapses every separately named PER-008 capability into a hidden
 * superuser flag. Revocation remains possible: the requested set may be narrower.
 */
export function canGrantCapabilities(
  held: readonly string[],
  requested: readonly Capability[],
): boolean {
  const ceiling = new Set(held);
  return requested.every((capability) => ceiling.has(capability));
}
